import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactPlayer from 'react-player/file';
import { useDebouncedResizeObserver } from '~/shared/hooks';
import { downloadHeatmapData } from '~/shared/services/api';
import styled from 'styled-components';
import { Paper, Fab } from '@mui/material';
import FilterBar from './FilterBar';
import { AlertBar, Loader } from '~/shared/components';
import { useNavAndFilters, useHeatmapData, useScroller } from '~/shared/services/charts';
import ExpandLess from '@mui/icons-material/ExpandLess';
import IndicatorLineChart from './Indicator/IndicatorLineChart';
import IndicatorStackedAreaChart from './Indicator/IndicatorStackedAreaChart';
import { tooltipCfg } from '~/shared/config/tooltipCfg';

const ScrollWrap = styled.div`
  flex: 1;
  overflow-y: scroll;
  height: 100%;
  max-width: 100%;
  ${({ theme }: any) => `
    padding: ${theme.spacing(3)}px;
  `};
`;

const VideoPlayerContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  height: 100%;
  text-align: center;
`;

const VideoPlayerControl = styled.div`
  height: 100%;
  width: 100%;
  padding: 0 10px;
  cursor: col-resize !important;
`;

const MainContainer = styled(Paper)`
  max-width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  padding: 4px;
`;

const HeatmapContainer = styled.div`
  flex: 1;
  position: relative;
  padding: 8px;
`;

const FabContainer = styled.div`
  position: relative;
  button {
    position: absolute;
    min-height: 26px;
    height: 26px;
    width: 26px;
    right: 2px;
    bottom: 2px;
    background: var(--background-chart-fab);
    color: var(--color-chart-fab);
  }
`;

const AlignedLoader = styled(Loader)`
  position: fixed;
  left: calc(50% - 25px);
  top: calc(50% - 25px);
`;

// eslint-disable-next-line @typescript-eslint/ban-types
function useHeatmapClick(navHirarchyItem: any, onScroll: Function, filterValues: any) {
  const [indicatorSections, setIndicatorSections] = useState<any[]>([]);
  useEffect(() => {
    setIndicatorSections([]);
  }, [navHirarchyItem, filterValues]);
  const onClick = useCallback(
    (cellInfo: { source: string; topic?: string; symbol?: string }) => {
      setIndicatorSections([]);
      setTimeout(
        () =>
          navHirarchyItem &&
          setIndicatorSections(
            navHirarchyItem.indicators[cellInfo.source].map((indicatorObj: any) => {
              const indicator = indicatorObj[0];
              const type = indicatorObj[1];
              return { indicator, cellInfo, type };
            }),
          ),
        10,
      );
      setTimeout(onScroll, 200);
    },
    [navHirarchyItem, onScroll],
  );

  return { indicatorSections, onClick };
}

export default function VideoPlayerPage({ navHirarchy, gridIndex, gridFilterValues, gridFilterValuesID }: any) {
  const playerRef = useRef<any>(null);
  const { scrollerRef, scrollToTop, scrollToBottom } = useScroller();
  const { category, subcategory, subsubcategory, prism, navHirarchyItem, filterValues, filterValuesID, prismId }: any =
    useNavAndFilters(navHirarchy, gridIndex);
  const { data, error, isLoading } = useHeatmapData(
    category,
    subcategory,
    subsubcategory,
    prism,
    gridFilterValuesID || filterValuesID,
    prismId,
  );
  const { ref: chartSizeRef }: any = useDebouncedResizeObserver(100);
  const { indicatorSections } = useHeatmapClick(navHirarchyItem, scrollToBottom, gridFilterValues || filterValues);
  const onDownload = () =>
    downloadHeatmapData(category, subcategory, subsubcategory, prism, filterValues, prismId).catch(e => {
      alert(e.message);
    });

  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(true);

  const handlePlayerMouseEnter = useCallback(() => {
    setIsVideoPlaying(false);
  }, []);

  const handlePlayerMouseLeave = useCallback(() => {
    setIsVideoPlaying(true);
  }, []);

  const [seeking, setSeeking] = useState<boolean>(false);

  const handleMouseMove = useCallback(
    (e: any) => {
      const seekToTime =
        (playerRef?.current?.player &&
          Math.floor(
            ((e.nativeEvent.offsetX < 0 ? 0 : e.nativeEvent.offsetX) /
              ((window.innerWidth - 260 - 260 - 110) / playerRef?.current?.player?.prevLoaded)) *
              100,
          ) / 100) ||
        Number(0);

      if (!seeking) {
        setSeeking(true);
        playerRef?.current.player.seekTo(seekToTime, 'seconds');
        setTimeout(() => setSeeking(false), seekToTime <= 30 ? 120 : seekToTime > 30 && seekToTime <= 50 ? 240 : 350);
      }
    },
    [seeking],
  );

  return (
    <ScrollWrap ref={scrollerRef}>
      {error ? <AlertBar message={(error as unknown as any).message} /> : <div />}
      <MainContainer>
        <ContentContainer>
          <HeatmapContainer ref={chartSizeRef}>
            <VideoPlayerContainer>
              {!data?.url || isLoading ? (
                <AlignedLoader />
              ) : (
                <VideoPlayerControl
                  onMouseEnter={handlePlayerMouseEnter}
                  onMouseLeave={handlePlayerMouseLeave}
                  onMouseMove={handleMouseMove}
                >
                  <ReactPlayer
                    ref={playerRef}
                    width="100%"
                    height="100%"
                    volume={0}
                    playing={isVideoPlaying}
                    muted={true}
                    loop={true}
                    url={data.url}
                    playbackRate={1}
                  />
                </VideoPlayerControl>
              )}
            </VideoPlayerContainer>
          </HeatmapContainer>
          {!gridIndex && gridIndex !== 0 && (
            <FilterBar
              type={navHirarchyItem?.type}
              filters={navHirarchyItem?.filters}
              values={gridFilterValues || filterValues}
              filterHierarchy={navHirarchyItem?.narrativeFilterTree}
              filterHierarchyValues={gridFilterValuesID || filterValuesID}
              onDownload={onDownload}
              tooltipCfg={tooltipCfg}
            />
          )}
        </ContentContainer>
      </MainContainer>
      {indicatorSections.map((section: any) => (
        <div key={section.indicator + section.topic + section.symbol}>
          {section.type === 'bar' && (
            <IndicatorStackedAreaChart
              key={section.indicator + section.topic + section.symbol}
              {...{ category, prism, prismId, ...section, filterValues, tooltipCfg }}
            ></IndicatorStackedAreaChart>
          )}
          {section.type === 'line' && (
            <IndicatorLineChart
              key={section.indicator + section.topic + section.symbol}
              {...{ category, prism, prismId, ...section, filterValues, tooltipCfg }}
            ></IndicatorLineChart>
          )}
        </div>
      ))}
      <FabContainer>
        {indicatorSections.length > 0 && (
          <Fab onClick={scrollToTop}>
            <ExpandLess />
          </Fab>
        )}
      </FabContainer>
    </ScrollWrap>
  );
}
